import * as React from "react"
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react"
import { Select as SelectPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

function Select({
	...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
	return <SelectPrimitive.Root data-slot="select" {...props} />
}

function SelectGroup({
	...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
	return <SelectPrimitive.Group data-slot="select-group" {...props} />
}

function SelectValue({
	...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
	return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

function SelectTrigger({
	className,
	size = "default",
	children,
	...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
	size?: "sm" | "default"
}) {
	return (
	<SelectPrimitive.Trigger
		data-slot="select-trigger"
		data-size={size}
		className={cn(
		"border-border data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive flex w-full items-center justify-between gap-2 rounded-xl border bg-secondary/60 backdrop-blur-sm px-4 text-sm font-medium shadow-xs transition-all duration-200 outline-none focus-visible:ring-[3px] hover:bg-secondary/90 disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-11 data-[size=sm]:h-9 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
		className
		)}
		{...props}
	>
		{children}
		<SelectPrimitive.Icon asChild>
		<ChevronDownIcon className="size-4 text-muted-foreground transition-transform duration-200" />
		</SelectPrimitive.Icon>
	</SelectPrimitive.Trigger>
	)
}

function SelectContent({
	className,
	children,
	position = "popper",
	align = "center",
	...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
	return (
	<SelectPrimitive.Portal>
		<SelectPrimitive.Content
		data-slot="select-content"
		className={cn(
			"bg-popover/80 backdrop-blur-xl text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-xl border border-border shadow-lg",
			position === "popper" &&
			"data-[side=bottom]:translate-y-1.5 data-[side=left]:-translate-x-1.5 data-[side=right]:translate-x-1.5 data-[side=top]:-translate-y-1.5",
			className
		)}
		position={position}
		align={align}
		sideOffset={4}
		{...props}
		>
		<SelectScrollUpButton />
		<SelectPrimitive.Viewport
			className={cn(
			"p-1.5",
			position === "popper" &&
				"h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1"
			)}
		>
			{children}
		</SelectPrimitive.Viewport>
		<SelectScrollDownButton />
		</SelectPrimitive.Content>
	</SelectPrimitive.Portal>
	)
}

function SelectLabel({
	className,
	...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
	return (
	<SelectPrimitive.Label
		data-slot="select-label"
		className={cn("text-muted-foreground px-3 py-2 text-xs font-medium uppercase tracking-wider", className)}
		{...props}
	/>
	)
}

function SelectItem({
	className,
	children,
	...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
	return (
	<SelectPrimitive.Item
		data-slot="select-item"
		className={cn(
		"focus:bg-accent focus:text-accent-foreground data-[state=checked]:bg-foreground/8 [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-lg py-2.5 pr-9 pl-3 text-sm font-medium outline-hidden select-none transition-colors duration-150 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
		className
		)}
		{...props}
	>
		<span
		data-slot="select-item-indicator"
		className="absolute right-3 flex size-4 items-center justify-center"
		>
		<SelectPrimitive.ItemIndicator>
			<CheckIcon className="size-4 text-foreground" />
		</SelectPrimitive.ItemIndicator>
		</span>
		<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
	</SelectPrimitive.Item>
	)
}

function SelectSeparator({
	className,
	...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
	return (
	<SelectPrimitive.Separator
		data-slot="select-separator"
		className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
		{...props}
	/>
	)
}

function SelectScrollUpButton({
	className,
	...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
	return (
	<SelectPrimitive.ScrollUpButton
		data-slot="select-scroll-up-button"
		className={cn(
		"flex cursor-default items-center justify-center py-1",
		className
		)}
		{...props}
	>
		<ChevronUpIcon className="size-4" />
	</SelectPrimitive.ScrollUpButton>
	)
}

function SelectScrollDownButton({
	className,
	...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
	return (
	<SelectPrimitive.ScrollDownButton
		data-slot="select-scroll-down-button"
		className={cn(
		"flex cursor-default items-center justify-center py-1",
		className
		)}
		{...props}
	>
		<ChevronDownIcon className="size-4" />
	</SelectPrimitive.ScrollDownButton>
	)
}

export {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectScrollDownButton,
	SelectScrollUpButton,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
}
